import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * GlassTable — compound table container matching the `.glass rounded-2xl` card
 * treatment from the design HTML (quotes.html lines 414–555).
 *
 * Composition:
 *   <GlassTable toolbar={<Toolbar />} footer={<Footer />}>
 *     <table className="nv-table">...</table>
 *   </GlassTable>
 *
 * Sub-components:
 *   GlassTable.Toolbar  — renders a `px-4 py-3 border-b bg-white/30` header bar
 *   GlassTable.Footer   — renders a `px-4 py-3 border-t bg-white/30` footer bar
 *
 * The `children` slot is wrapped in `overflow-x-auto` so wide tables scroll
 * horizontally inside the glass surface.
 */
export interface GlassTableProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Rendered above the table inside the glass card (search bar, filter btns) */
  toolbar?: React.ReactNode;
  /** Rendered below the table (pagination, totals summary) */
  footer?: React.ReactNode;
}

const GlassTableRoot = React.forwardRef<HTMLDivElement, GlassTableProps>(
  ({ className, toolbar, footer, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('glass rounded-2xl overflow-hidden', className)}
      {...props}
    >
      {toolbar}
      <div className="overflow-x-auto">{children}</div>
      {footer}
    </div>
  ),
);
GlassTableRoot.displayName = 'GlassTable';

// ── Toolbar sub-component ────────────────────────────────────────────────────

export type GlassTableToolbarProps = React.HTMLAttributes<HTMLDivElement>;

const GlassTableToolbar = React.forwardRef<
  HTMLDivElement,
  GlassTableToolbarProps
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'px-4 py-3 border-b border-[var(--line,rgba(226,232,240,1))]/40',
      'flex flex-wrap items-center gap-2.5 bg-white/30',
      className,
    )}
    {...props}
  />
));
GlassTableToolbar.displayName = 'GlassTable.Toolbar';

// ── Footer sub-component ─────────────────────────────────────────────────────

export type GlassTableFooterProps = React.HTMLAttributes<HTMLDivElement>;

const GlassTableFooter = React.forwardRef<
  HTMLDivElement,
  GlassTableFooterProps
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'px-4 py-3 border-t border-[var(--line,rgba(226,232,240,1))]/40',
      'flex items-center justify-between text-[12.5px] text-ink-muted bg-white/30',
      className,
    )}
    {...props}
  />
));
GlassTableFooter.displayName = 'GlassTable.Footer';

// ── Compound export ──────────────────────────────────────────────────────────

export const GlassTable = Object.assign(GlassTableRoot, {
  Toolbar: GlassTableToolbar,
  Footer: GlassTableFooter,
});
